import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import APIS from "../../constant/Endpoint";
import Card from "../Reusable/Card";
import DataTable from "../Reusable/DataTable";
import NACell from "../Reusable/DataTable/NACell";
import { classNames } from "../Reusable/DatePicker/helpers";
import DateRangePicker from "../Reusable/DateRangerPicker";
//@ts-ignore
import { tw } from "@careinfo/tailwind";
import { useSessionStorage } from "@uidotdev/usehooks";
import { IFilterInvoice } from "../../types/interface";
import MultiSelectFilterOrderStatus from "../Reusable/MultiSelectFilterOrderStatus";
import ComboboxFilter from "../Reusable/ComboboxFilter";
import { hasValues } from "../../utils/hasValues";
import InvoiceDataTable from "../Reusable/MerchantInvoiceDataTable";

const OverView = () => {
  const [localState, handleSetState] = useSessionStorage<IFilterInvoice>(
    "invoice-finance-filter",
    {
      delivery_status__in: null,
      pickup_branch: null,
      to_branch: null,
      payment_method: null,
      pickup_branch__warehouse: null,
      destination_branch__warehouse: null,
      vendor: null,
      created__range: null,
      sender: null,
      receiver: null,
      rider: null,
      staff: null,
      destination: null,
    }
  );

  const { setValue, getValues, reset, watch, control } =
    useForm<IFilterInvoice>({
      defaultValues: async () => ({
        delivery_status__in: localState?.delivery_status__in ?? null,
        pickup_branch: localState?.pickup_branch ?? null,
        to_branch: localState?.to_branch ?? null,
        payment_method: localState?.payment_method ?? null,
        pickup_branch__warehouse: localState?.pickup_branch__warehouse ?? null,
        destination_branch__warehouse:
          localState?.destination_branch__warehouse ?? null,
        vendor: localState?.vendor ?? null,
        created__range: localState?.created__range ?? null,
        sender: localState?.sender ?? null,
        receiver: localState?.receiver ?? null,
        rider: localState?.rider ?? null,
        destination: localState?.destination ?? null,
        staff: localState?.staff ?? null,
      }),
    });
  const [selectedOrder, setSelectedOrder] = useState("");
  const [openReviewModal, setOpenReviewModal] = useState(false);
  const [isFilterVisible, setFilterVisiable] = useState(hasValues(localState));

  const handleFilterReset = () => {
    reset({
      pickup_branch: null,
      to_branch: null,
      payment_method: null,
      pickup_branch__warehouse: null,
      destination_branch__warehouse: null,
      vendor: null,
      created__range: null,
      sender: null,
      receiver: null,
      rider: null,
      staff: null,
      destination: null,
    });
  };

  let defaultColumns: any = [
    {
      header: "WayBill",
      accessorKey: "tracking_number",
      cell: ({ row, getValue }: any) => {
        return (
          <div>
            <p
              className={classNames(
                row?.original?.is_duplicate ? tw`text-red-600` : "",
                tw`whitespace-nowrap`
              )}
            >
              {getValue() ?? "N/A"}
            </p>
            <p className={tw`text-xs text-gray-500`}>
              {moment(row?.original?.created).format("YY-MM-DD HH:mm:ss")}
            </p>
          </div>
        );
      },
    },
    {
      header: "Origin",
      accessorKey: "pickup_branch.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.pickup_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Sender",
      accessorKey: "sender.full_name",
      cell: NACell,
    },
    {
      header: "Receiver",
      accessorKey: "receiver.full_name",
      cell: NACell,
    },
    {
      header: "Destination",
      accessorKey: "destination.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`uppercase`}>{getValue()}</p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.destination_branch?.warehouse?.name}
            </p>
          </>
        );
      },
    },
    {
      header: "Finance",
      accessorKey: "deliver_charge",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p className={tw`whitespace-nowrap`}>
              COD: {row?.original?.cod ?? 0.0}{" "}
            </p>
            <p className={tw`whitespace-nowrap`}>
              Collected: {row?.original?.collected_cod ?? 0.0}
            </p>
            <p className={tw`whitespace-nowrap`}>D. Charge:{getValue()}</p>
          </>
        );
      },
    },
    {
      header: "Piece",
      accessorKey: "total_piece",
      cell: NACell,
    },
    {
      header: "Remarks",
      accessorKey: "remark",
      cell: ({ row, getValue }: any) => (
        <button
          onClick={() => {
            setSelectedOrder(row?.original?.id);
            setOpenReviewModal(true);
          }}
        >
          {getValue()} Remarks
        </button>
      ),
    },
    {
      header: "Status",
      accessorKey: "delivery_status.name",
      cell: ({ row, getValue }: any) => {
        return (
          <>
            <p
              className={tw`uppercase px-1 text-center rounded-md`}
              style={{ background: row?.original?.delivery_status?.color }}
            >
              {getValue()}
            </p>
            <p className={tw`text-xs text-primary`}>
              {row?.original?.rider?.name}
            </p>
          </>
        );
      },
    },
  ];

  const filterParams = watch();

  const handleRowClick = (row) => {
    window.location.replace(`/order#/edit/${row?.original?.id}`);
  };

  useEffect(() => {
    handleSetState({
      ...localState,
      ...filterParams,
    });
  }, [filterParams]); //eslint-disable-line

  const params = useCallback(() => {
    const newValue = {};
    for (const [key, value] of Object.entries(filterParams)) {
      if (value) {
        if (Array.isArray(value)) {
          newValue[key] = value.map((v) => v.value).join(",");
        } else if (typeof value === "object") {
          //@ts-ignore
          newValue[key] = value.id;
        } else if (!value) {
          newValue[key] = null;
        } else {
          newValue[key] = value;
        }
      }
    }
    return newValue;
  }, [filterParams]);

  return (
    <>
      <Card title="Filter" onClick={() => setFilterVisiable(!isFilterVisible)}>
        <div
          className={classNames(
            isFilterVisible ? tw`block` : tw`hidden`,
            tw`relative`
          )}
        >
          {isFilterVisible ? (
            <div className={tw`grid grid-cols-3 md:grid-cols-3 gap-x-4 mx-4`}>
              <MultiSelectFilterOrderStatus<IFilterInvoice>
                label={"Status"}
                queryKey={"delivery_status__in"}
                params={{
                  is_active: true,
                }}
                name={"delivery_status__in"}
                control={control}
                url={`${APIS.ORDER_STATUS}`}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.BRANCH}
                queryKey={"branch-pickup"}
                name={"pickup_branch"}
                label={"From Branch"}
                params={{
                  is_ative: true,
                }}
                placeholder={"Select From Branch"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.BRANCH}
                params={{ level: 3, is_ative: true }}
                queryKey={"branch-to"}
                name={"to_branch"}
                label={"To Branch"}
                placeholder={"Select Branch"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.LOCATION}
                queryKey={"destination"}
                name={"destination"}
                label={"Destination"}
                params={{
                  is_ative: true,
                }}
                placeholder={"Select destination"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.PAYMENT_METHOD_TYPE}
                queryKey={"payment-method-type"}
                params={{
                  is_ative: true,
                }}
                name={"payment_method"}
                label={"Payment Method"}
                placeholder={"Select Payment Method"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.WAREHOUSE}
                queryKey={"warehouse"}
                name={"pickup_branch__warehouse"}
                params={{
                  is_ative: true,
                }}
                label={"Origin Warehouse"}
                placeholder={"Select Origin Warehouse"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.WAREHOUSE}
                queryKey={"warehouse"}
                name={"destination_branch__warehouse"}
                params={{
                  is_ative: true,
                }}
                label={"Destination Warehouse"}
                placeholder={"Select Destination Warehouse"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.MERCHANT}
                queryKey={"merchant"}
                params={{
                  is_ative: true,
                }}
                name={"vendor"}
                label={"Vendor"}
                placeholder={"Select Vendor"}
                control={control}
              />
              <DateRangePicker
                label={"Select Date Range"}
                setValue={setValue}
                name={"created__range"}
                getValues={getValues}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.CUSTOMER}
                queryKey={"customer"}
                name={"sender"}
                label={"Sender"}
                params={{
                  is_ative: true,
                }}
                placeholder={"Select Sender"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.CUSTOMER}
                queryKey={"customer"}
                name={"receiver"}
                label={"Receiver"}
                params={{
                  is_ative: true,
                }}
                placeholder={"Select Receiver"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.RIDER}
                queryKey={"rider"}
                name={"rider"}
                label={"Rider"}
                params={{
                  is_ative: true,
                }}
                placeholder={"Select Rider"}
                control={control}
              />
              <ComboboxFilter<IFilterInvoice>
                url={APIS.STAFF}
                queryKey={"staff"}
                name={"staff"}
                label={"Staff"}
                params={{
                  is_ative: true,
                  is_staff: true,
                }}
                placeholder={"Select Staff"}
                control={control}
              />
              <button
                className={tw`absolute -top-5 right-5`}
                onClick={handleFilterReset}
              >
                Reset
              </button>
            </div>
          ) : (
            ""
          )}
        </div>
      </Card>
      <InvoiceDataTable
        url={APIS.INVOICE_ORDER}
        queryKey={"overview-order"}
        columns={defaultColumns}
        tableName={"overview-orders"}
        isUpload={false}
        params={params()}
        handleRowClick={handleRowClick}
      />
    </>
  );
};

export default OverView;
